import React, { useMemo } from "react";
import { Box, Typography, Divider } from "@mui/material";

import useAuth from "../../hooks/useAuth";
import { ENGLISH_PILLARS, MATH_PILLARS } from "../../data/pillars";
import { estimatePillarScore } from "../../utils/analytics/estimatePillarScore";
import { estimateTestScore } from "../../utils/analytics/estimateTestScore";

/* ---------- helpers ---------- */

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

/* ---------- component ---------- */

const StudentOutputPage = () => {
  const { user } = useAuth();

  const englishSkillBands =
    user?.analytics?.english?.skills
      ? Object.fromEntries(
          Object.entries(user.analytics.english.skills).map(
            ([k, v]) => [k, v.band]
          )
        )
      : {};

  const mathSkillBands =
    user?.analytics?.math?.skills
      ? Object.fromEntries(
          Object.entries(user.analytics.math.skills).map(
            ([k, v]) => [k, v.band]
          )
        )
      : {};

  /* -------- English pillars -------- */

  const englishPillars = useMemo(() => {
    return ENGLISH_PILLARS.map((pillar) => {
      const rows = estimatePillarScore({
        pillar,
        skillBandMap: englishSkillBands,
        totalQuestions: 66,
      });

      return {
        id: pillar.id,
        name: pillar.name,
        pillarWeight: pillar.pillarWeight,
        correctLow: sum(rows.map((r) => r.correctLow || 0)),
        correctEst: sum(rows.map((r) => r.correctEst || 0)),
        correctHigh: sum(rows.map((r) => r.correctHigh || 0)),
      };
    });
  }, [englishSkillBands]);

  /* -------- Math pillars -------- */

  const mathPillars = useMemo(() => {
    return MATH_PILLARS.map((pillar) => {
      const rows = estimatePillarScore({
        pillar,
        skillBandMap: mathSkillBands,
        totalQuestions: 54,
      });

      return {
        id: pillar.id,
        name: pillar.name,
        pillarWeight: pillar.pillarWeight ?? 0.25, // until added
        correctLow: sum(rows.map((r) => r.correctLow || 0)),
        correctEst: sum(rows.map((r) => r.correctEst || 0)),
        correctHigh: sum(rows.map((r) => r.correctHigh || 0)),
      };
    });
  }, [mathSkillBands]);

  /* -------- Overall estimates -------- */

  const englishTotal = useMemo(
    () =>
      estimateTestScore({
        pillars: englishPillars,
        section: "reading_writing",
        maxQuestions: 66,
      }),
    [englishPillars]
  );

  const mathTotal = useMemo(
    () =>
      estimateTestScore({
        pillars: mathPillars,
        section: "math",
        maxQuestions: 54,
      }),
    [mathPillars]
  );

  /* ---------------- render ---------------- */

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Student Output — SAT Score Estimates
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6">Overall SAT</Typography>

      <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: "rgba(0,0,0,0.06)", maxWidth: 420 }}>
        <Typography>
          Reading & Writing:{" "}
          <strong>
            {englishTotal.scaled.low} – {englishTotal.scaled.est} –{" "}
            {englishTotal.scaled.high}
          </strong>
        </Typography>

        <Typography>
          Math:{" "}
          <strong>
            {mathTotal.scaled.low} – {mathTotal.scaled.est} –{" "}
            {mathTotal.scaled.high}
          </strong>
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Typography fontWeight={800}>
          Total SAT:{" "}
          <strong>
            {englishTotal.scaled.low + mathTotal.scaled.low} –{" "}
            {englishTotal.scaled.est + mathTotal.scaled.est} –{" "}
            {englishTotal.scaled.high + mathTotal.scaled.high}
          </strong>
        </Typography>
      </Box>
    </Box>
  );
};

export default StudentOutputPage;
